"use client";

import { createContext, useContext, useState, ReactNode } from "react";
import { Toast, ToastToggle } from "flowbite-react";
import { HiCheck, HiExclamation, HiX } from "react-icons/hi";

type ToastType = "success" | "warning" | "error";

interface ToastMessage {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = (message: string, type: ToastType = "success") => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);
    
    // Auto-dismiss after 3 seconds
    setTimeout(() => removeToast(id), 10000);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      
      {/* Toast Container */}
      <div className="fixed bottom-5 right-5 z-50 flex flex-col gap-2">
        {toasts.map((t) => (
          <Toast key={t.id}>
            <div className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
              t.type === 'success' ? 'bg-green-100 text-green-500' : 
              t.type === 'warning' ? 'bg-orange-100 text-orange-500' : 
              'bg-red-100 text-red-500'
            }`}>
              {t.type === 'success' && <HiCheck className="h-5 w-5" />}
              {t.type === 'warning' && <HiExclamation className="h-5 w-5" />}
              {t.type === 'error' && <HiX className="h-5 w-5" />}
            </div>
            <div className="ml-3 text-sm font-normal">{t.message}</div>
            <ToastToggle onDismiss={() => removeToast(t.id)} />
          </Toast>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within a ToastProvider");
  return context;
};


